import React from "react";
import { Info, SlidersHorizontal, SquarePen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { StrategyRdCoachChip } from "@/types/strategyRdCoach";
import type { StrategyRdCoachStrategySummary } from "@/utils/strategyRdCoachReplySections";
import { strategyRdBrandActionButtonClassName } from "../strategyRdCardShell";
import { statsCardHeadlineClassName } from "../../Statistics/components/statisticsCardHeadline";

/**
 * "What was actually tested", as a card: the rules TES ran, what's not included yet, and the
 * reply's own Lab links (See your backtest settings / See your recipe) as its buttons.
 */
export function StrategyRdCoachStrategyCard({
  strategy,
  name,
  version,
  chips,
  chipsDisabled,
  onChipClick,
}: {
  strategy: StrategyRdCoachStrategySummary;
  name?: string | null;
  version?: number | null;
  /** The reply's chips — only the recipe / backtest-settings links show here. */
  chips?: StrategyRdCoachChip[] | null;
  chipsDisabled?: boolean;
  onChipClick?: (chip: StrategyRdCoachChip) => void;
}): JSX.Element {
  const settings = chips?.find((c) => c.id === "lab_open_backtest_settings");
  const recipe = chips?.find((c) => c.id === "lab_open_recipe");
  const setup = strategy.rows.find((r) => r.key === "Setup")?.value;
  return (
    <div
      className="@container/strategycard overflow-hidden rounded-[8px] !border !border-solid !border-border bg-card font-[family-name:var(--font-primary)]"
      data-strategy-rd-coach-strategy=""
    >
      <div className="flex items-start justify-between gap-3 !px-4 !pb-3 !pt-3.5">
        <div className="min-w-0">
          <p className="!m-0 truncate !text-[15px] !font-medium leading-6 text-[color:var(--brand-tropical-surf)]">
            {name || setup || "Your strategy"}
          </p>
          {strategy.market ? (
            <p className="!m-0 !mt-0.5 font-[family-name:var(--font-secondary)] text-[11px] tabular-nums text-gray-400">
              {strategy.draft ? strategy.market : `Tested on ${strategy.market}`}
            </p>
          ) : null}
        </div>
        {strategy.draft || version != null ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-[4px] bg-muted px-1.5 py-px text-[10px] font-medium text-gray-500">
            {strategy.draft ? (
              <>
                <span className="size-1.5 rounded-full bg-gray-400" aria-hidden />
                Draft
              </>
            ) : null}
            {strategy.draft && version != null ? " · " : null}
            {version != null ? `v${version}` : null}
          </span>
        ) : null}
      </div>
      <dl className="!m-0 grid grid-cols-1 gap-x-4 gap-y-2.5 !border-0 !border-t !border-solid !border-border !px-4 !py-3.5 @[24rem]/strategycard:grid-cols-[7rem_minmax(0,1fr)]">
        {strategy.rows.map((row) => (
          <React.Fragment key={row.key}>
            <dt className={cn(statsCardHeadlineClassName, "!text-[11px] !leading-5")}>{row.key}</dt>
            <dd className="!m-0 -mt-1.5 text-[13px] leading-5 text-gray-600 @[24rem]/strategycard:!mt-0 dark:text-foreground">
              {row.value}
            </dd>
          </React.Fragment>
        ))}
      </dl>
      {strategy.notIncluded.length > 0 ? (
        <div className="!px-4 !pb-3.5">
          <p className="!m-0 flex items-start gap-2 rounded-[6px] bg-muted/60 !px-3 !py-2 text-xs leading-5 text-gray-500 dark:text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <span>Not included yet: {strategy.notIncluded.join(", ")}</span>
          </p>
        </div>
      ) : null}
      {(settings || recipe) && onChipClick ? (
        <div className="flex flex-wrap items-center justify-end gap-2 !border-0 !border-t !border-solid !border-border bg-muted/30 !px-4 !py-3">
          {settings ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                strategyRdBrandActionButtonClassName,
                "h-8 !px-2.5 text-xs normal-case tracking-normal !text-gray-500 hover:!text-[color:var(--brand-tropical-surf)] [&_svg]:!text-current [&>span]:!normal-case"
              )}
              disabled={chipsDisabled}
              onClick={() => onChipClick(settings)}
            >
              <SlidersHorizontal className="size-3.5" aria-hidden />
              Backtest settings
            </Button>
          ) : null}
          {recipe ? (
            <Button
              type="button"
              variant="outline-brand"
              size="sm"
              className={cn(strategyRdBrandActionButtonClassName, "h-8 !px-3 text-xs normal-case tracking-normal [&>span]:!normal-case")}
              disabled={chipsDisabled}
              onClick={() => onChipClick(recipe)}
            >
              <SquarePen className="size-3.5" aria-hidden />
              Edit recipe
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
